import {
  AdminCustomProviderSelect,
  AdminDollarInput2,
  AdminInput,
  AdminSelect,
  Button,
  Div,
  ErrorToast,
  H5,
  Icon,
  Modal,
  PlusIcon,
  PropsModal,
  SuccessToast,
  TrashIconRed,
  createMenuModifierGroup,
  menuItemDescriptionMaxLength,
  menuItemNameMaxLength,
  updateMenuModifierGroup,
  updateMenuModifierOptionsOrder,
  validateMenuItemDescription,
  validateMenuItemName,
  reportError,
} from '@vgrubs/components';
import {
  CreateMenuModifierV2Dto,
  ModifierType,
  UpdateMenuModifierGroupV2Dto,
} from 'components/src/types/api/menu.types';
import {
  Availability,
  MenuOptionPriceCustomizationV2Response,
  MenuV2Response,
  ModifierGroupItemV2Response,
  ModifierItemV2Response,
} from 'components/src/types/menu.types';
import { Provider } from 'components/src/types/shared.types';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router';
import {
  DraftModifierOption,
  optionsOrderAfterSave,
  toDraftOption,
} from '../../helpers/modifierOptionsOrder';
import { Collapsible } from '../../MenyStyled';
import { useSelectedMenu } from '../../menuSelection';
import { SortableModifierOptions } from './SortableModifierOptions';

const options2 = [
  { name: 'Price Group', value: ModifierType.Price },
  { name: 'Option Group', value: ModifierType.Option },
];

const typeOptions = (
  <optgroup label="Select Group Type">
    {options2.map((r, i) => (
      <option value={r.value} key={i}>
        {r.name}
      </option>
    ))}
  </optgroup>
);

const requiredOptions = [
  { name: 'No', value: 'false' },
  { name: 'Yes', value: 'true' },
];

const requiredOptionsGroup = (
  <optgroup label="Select Group Type">
    {requiredOptions.map((r, i) => (
      <option value={r.value} key={i}>
        {r.name}
      </option>
    ))}
  </optgroup>
);

const choicesOptions = [
  { name: 'Only One', value: 'Only One' },
  { name: 'Set a min-max', value: 'Set a min-max' },
];

const choicesOptionsGroup = (
  <optgroup label="Select Group Type">
    {choicesOptions.map((r, i) => (
      <option value={r.value} key={i}>
        {r.name}
      </option>
    ))}
  </optgroup>
);

export const AddNewModifierGroupModal = ({
  addNewModal,
  closeModal,
  setMenu,
  setLoading,
  edit,
  duplicate,
  handleChangeShouldPublish,
}: PropsModal & {
  addNewModal: boolean | ModifierGroupItemV2Response | null;
  setMenu: Dispatch<SetStateAction<MenuV2Response | null>>;
  edit?: boolean;
  duplicate?: boolean;
  handleChangeShouldPublish: (shouldPublish: boolean) => void;
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const [optionGroup, setOptionGroup] = useState<ModifierType>(
    options2[0].value as ModifierType
  );
  const [isRequired, setIsRequired] = useState(requiredOptions[0].value);
  const [choices, setChoices] = useState(choicesOptions[0].value);

  const [selectionsMin, setSelectionsMin] = useState<number | string>('');
  const [selectionsMax, setSelectionsMax] = useState<number | string>('');

  const [optionName, setOptionName] = useState('');
  const [optionPrice, setOptionPrice] = useState(0);
  const [optionCustomizedPrices, setOptionCustomizedPrices] = useState<
    MenuOptionPriceCustomizationV2Response[]
  >([]);
  const [editingOption, setEditingOption] = useState<number | null>(null);

  const [availability, setAvailability] = useState(Availability.Available);

  const handleValueTotalChange = useCallback((val: number) => {
    setOptionPrice(val);
  }, []);

  const [options, setOptions] = useState<DraftModifierOption[]>([]);

  const ref = useRef<HTMLDivElement>(null);
  const [elementHeight, setElementHeight] = useState<number | undefined>();
  const [expanded, setExpanded] = useState(false);

  const { locationId, brandId } = useParams() as {
    locationId: string;
    brandId: string;
  };
  const { menuId } = useSelectedMenu();

  const required = isRequired === 'true';

  const data = addNewModal;

  useEffect(() => {
    if ((edit || duplicate) && typeof data === 'object') {
      setName(data?.name || '');
      setDescription(data?.description || '');
      setOptionGroup(data?.type || ModifierType.Price);
      setIsRequired(data?.required ? 'true' : 'false');
      setOptions((data?.options || []).map(toDraftOption));
      setSelectionsMin(data?.selectionsMin || '');
      setSelectionsMax(data?.selectionsMax || '');

      if (data?.required) {
        if (data?.selectionsMin === 1 && data?.selectionsMax === 1) {
          setChoices('Only One');
        } else {
          setChoices('Set a min-max');
        }
      } else {
        if (data?.selectionsMin === 0 && data?.selectionsMax === 1) {
          setChoices('Only One');
        } else {
          setChoices('Set a min-max');
        }
      }
    } else {
      emptyForm();
    }
  }, [data]);

  useEffect(() => {
    if (ref.current) setElementHeight(ref.current.clientHeight);
  }, [
    expanded,
    editingOption,
    optionCustomizedPrices.length,
    optionGroup,
    optionName,
    availability,
  ]);

  const emptyForm = () => {
    setName('');
    setDescription('');
    setOptionGroup(options2[0].value as ModifierType);
    setIsRequired(requiredOptions[0].value);
    setChoices(choicesOptions[0].value);
    setOptions([]);
    setSelectionsMin('');
    setSelectionsMax('');
  };

  async function handleAddModifierGroup() {
    if (!name) return ErrorToast('Please enter modifier group name');
    if (!validateMenuItemName(name)) return;
    if (!validateMenuItemDescription(description)) return;
    if (options.length < 1) return ErrorToast('Please add options');
    if (optionGroup === ModifierType.Option && selectionsMin > selectionsMax)
      return ErrorToast(
        'Maximum quantity must be grater then or equal to minimum'
      );

    // prettier-ignore
    if (optionGroup === ModifierType.Option && required && (selectionsMin !== null && Number(selectionsMin) < 1))
      return ErrorToast(
        `If it's required choice, Minimum Qty can't be les than 1`
      );

    const body: CreateMenuModifierV2Dto = {
      name,
      description,
      type: optionGroup,
      options: options.map((o) => ({
        name: o.name,
        // description: o.description,
        price: o.price,
        availability: o.availability,
        customizedPrices: o.customizedPrices ?? [],
      })),
      required,
      selectionsMin: Number(selectionsMin),
      selectionsMax: Number(selectionsMax),
    };

    const bodyEdit: UpdateMenuModifierGroupV2Dto = {
      name,
      description,
      type: optionGroup,
      options: options.map((o) => ({
        id: o.id,
        name: o.name,
        // description: o.description,
        price: o.price,
        availability: o.availability,
        customizedPrices: o.customizedPrices ?? [],
      })),
      required,
      selectionsMin: Number(selectionsMin),
      selectionsMax: Number(selectionsMax),
    };

    const group =
      edit && typeof addNewModal === 'object' && addNewModal
        ? addNewModal
        : null;

    try {
      setLoading?.(true);
      const { data } = group
        ? await updateMenuModifierGroup(locationId, brandId, group.id, bodyEdit)
        : await createMenuModifierGroup(locationId, brandId, body, menuId);

      setMenu(data?.data);
      handleChangeShouldPublish(true);
      if (group && !(await saveOptionsOrder(group, data?.data))) return;

      emptyForm();
      SuccessToast(data?.message);
      closeModal?.();
    } catch (error) {
      reportError(error);
    } finally {
      setLoading?.(false);
    }
  }

  /**
   * Saving the group keeps existing options where they were and appends new
   * ones, so a drag and drop order is a second request, sent once every
   * option has an id. On failure the popup stays open showing the saved
   * order, and false is returned.
   */
  async function saveOptionsOrder(
    group: ModifierGroupItemV2Response,
    savedMenu: MenuV2Response
  ) {
    const saved =
      savedMenu?.modifierGroups?.find((g) => g.id === group.id)?.options ?? [];
    try {
      const list = optionsOrderAfterSave(
        options,
        saved,
        new Set(group.options.map((o) => o.id))
      );
      if (list) {
        const { data } = await updateMenuModifierOptionsOrder(
          locationId,
          brandId,
          group.id,
          list
        );
        setMenu(data?.data);
      }
      return true;
    } catch (error) {
      setOptions(saved.map(toDraftOption));
      reportError(error);
      return false;
    }
  }

  const validateCustomizedPrices = (
    prices: MenuOptionPriceCustomizationV2Response[]
  ) => {
    for (const p of prices) {
      if (!p.provider) {
        ErrorToast('Please select provider for all custom prices');
        return false;
      }
    }
    return true;
  };

  const handleAddOption = () => {
    if (!optionName) return ErrorToast('Please enter modifier name');
    if (!validateMenuItemName(optionName)) return;
    if (!validateCustomizedPrices(optionCustomizedPrices)) return;

    setOptions((prev) => [
      ...prev,
      toDraftOption({
        name: optionName,
        price: optionPrice,
        availability,
        customizedPrices: optionCustomizedPrices,
      } as ModifierItemV2Response),
    ]);
    setOptionName('');
    setOptionPrice(0);
    setExpanded(false);
    setAvailability(Availability.Available);
    setOptionCustomizedPrices([]);
  };

  const handleRemoveOption = (i: number) => {
    const arr = [...options];

    if (i > -1) {
      arr.splice(i, 1);
    }

    setOptions(arr);
  };

  const handleChangeIsRequired = (v: 'true' | 'false') => {
    const required = v === 'true';

    setIsRequired(v);
    if (required) {
      setSelectionsMin(1);
    } else {
      setSelectionsMin(0);
    }

    if (choices === 'Only One') {
      if (required) {
        setSelectionsMin(1);
        setSelectionsMax(1);
      } else {
        setSelectionsMin(0);
        setSelectionsMax(1);
      }
    }
    if (choices === 'Set a min-max') {
      if (required) {
        setSelectionsMin(1);
        setSelectionsMax(1);
      } else {
        setSelectionsMin(0);
        setSelectionsMax(0);
      }
    }
  };

  const handleChangeChoices = (v: string) => {
    setChoices(v);
    if (v === 'Only One') {
      if (required) {
        setSelectionsMin(1);
        setSelectionsMax(1);
      } else {
        setSelectionsMin(0);
        setSelectionsMax(1);
      }
    }
    if (v === 'Set a min-max') {
      if (required) {
        setSelectionsMin(1);
        setSelectionsMax(1);
      } else {
        setSelectionsMin(0);
        setSelectionsMax(0);
      }
    }
  };

  const handleChangeOptionGroup = (v: ModifierType) => {
    setOptionGroup(v);
    if (v === ModifierType.Price) {
      handleChangeIsRequired('true');
    }
    if (v === ModifierType.Option) {
      handleChangeIsRequired('false');
    }
  };

  const handleEditOption = () => {
    if (!optionName) return ErrorToast('Please enter modifier name');
    if (!validateMenuItemName(optionName)) return;
    if (!validateCustomizedPrices(optionCustomizedPrices)) return;

    setOptions((prev) =>
      prev.map((o, i) =>
        i === editingOption
          ? {
              ...o,
              name: optionName,
              price: optionPrice,
              availability: availability,
              customizedPrices: optionCustomizedPrices,
            }
          : o
      )
    );
    setOptionName('');
    setOptionPrice(0);
    setAvailability(Availability.Available);
    setOptionCustomizedPrices([]);
    setEditingOption(null);
  };

  const handleExpand = () => {
    setExpanded((prev) => !prev);
    handleValueTotalChange(0);
    setOptionName('');
    setOptionCustomizedPrices([]);
  };

  const handleEditExpand = (option: ModifierItemV2Response, index: number) => {
    setEditingOption(index);
    setOptionName(option.name);
    setOptionPrice(option.price);
    setAvailability(option.availability);
    setOptionCustomizedPrices(option.customizedPrices ?? []);
  };

  const handleEditCancelExpand = () => {
    setEditingOption(null);
    setOptionName('');
    setOptionPrice(0);
    setAvailability(Availability.Available);
    setOptionCustomizedPrices([]);
  };

  const handleAddCustomizedPriceRow = () => {
    setOptionCustomizedPrices((prev) => [
      ...prev,
      { provider: null as unknown as Provider, price: 0 },
    ]);
  };

  const handleChangeCustomizedPriceProvider = (
    index: number,
    provider: Provider | null
  ) => {
    setOptionCustomizedPrices((prev) =>
      prev.map((p, i) =>
        i === index ? { ...p, provider: provider as Provider } : p
      )
    );
  };

  const handleChangeCustomizedPricePrice = (index: number, price: number) => {
    setOptionCustomizedPrices((prev) =>
      prev.map((p, i) => (i === index ? { ...p, price } : p))
    );
  };

  const handleRemoveCustomizedPrice = (index: number) => {
    setOptionCustomizedPrices((prev) => prev.filter((_, i) => i !== index));
  };

  const allProviders: Provider[] = [Provider.vGrubs, Provider.bytes];

  const availabilityOptions = (
    <optgroup label="Select Availability">
      <option value={Availability.Available}>In Stock</option>
      <option value={Availability.SoldOut}>Sold Out (Indefinitely)</option>
    </optgroup>
  );
  return (
    <Modal
      isOpen={!!addNewModal}
      closeModal={closeModal}
      header={`${
        duplicate ? 'Duplicate' : edit ? 'Edit' : 'Add'
      } Modifier Group`}
      logo={false}
      zIndex={101}
      width="800px"
    >
      <Div $flex $column $gap="24px" $gapSM="16px">
        <Div $flex $columnSM $alignCenter $gapSM="16px" $gap="24px">
          <AdminInput
            placeholder="Enter Here"
            label="Group Name"
            $textCenter
            value={name}
            onChange={({ target }: { target: HTMLInputElement }) =>
              setName(target.value)
            }
            maxLength={menuItemNameMaxLength}
          />
          <AdminSelect
            value={optionGroup}
            options={typeOptions}
            onChange={({ target }: { target: HTMLSelectElement }) =>
              handleChangeOptionGroup(target.value as ModifierType)
            }
            label="Choose Group Type"
            $textCenter
            disabled={edit}
          />{' '}
        </Div>
        <AdminInput
          placeholder="Add context or internal names for this modifier group. Notes won't be."
          label="Notes"
          $textCenter
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          $textareaHeight={150}
          maxLength={menuItemDescriptionMaxLength}
        />

        <div>
          <Div
            $mt="16"
            $flex
            $between
            $alignCenter
            $gap="16px"
            $mb={options.length > 0 ? '16' : '0'}
          >
            <H5 $m="0">Modifiers</H5>

            {!expanded && (
              <Icon $pointer onClick={handleExpand}>
                <PlusIcon />
              </Icon>
            )}
          </Div>

          <Collapsible
            expanded={expanded || editingOption !== null}
            elementHeight={elementHeight}
          >
            <div ref={ref}>
              <Div
                $box={expanded || editingOption !== null}
                $p="24px 16px"
                $flex
                $column
                $alignCenter
                $gap="24px"
                $gapSM="16px"
                $width="100%"
              >
                <Div
                  $flex
                  $alignCenter
                  $columnSM
                  $gapSM="16px"
                  $gap="24px"
                  $width="100%"
                >
                  <AdminInput
                    placeholder="Enter Here"
                    label="Modifier Name"
                    $textCenter
                    value={optionName}
                    onChange={({ target }: { target: HTMLInputElement }) =>
                      setOptionName(target.value)
                    }
                    maxLength={menuItemNameMaxLength}
                  />

                  <AdminDollarInput2
                    label="Default Price"
                    $textCenter
                    onValueChange={handleValueTotalChange}
                    value={optionPrice}
                  />
                </Div>
                <AdminSelect
                  value={availability}
                  options={availabilityOptions}
                  onChange={({ target }: { target: HTMLSelectElement }) => {
                    setAvailability(target.value as Availability);
                  }}
                  label="Availability"
                  $textCenter
                />

                <Div $width="100%">
                  <Div $flex $between $alignCenter $mb="12">
                    <H5 $m="0">Price per Providers</H5>
                    {optionCustomizedPrices.length < allProviders.length && (
                      <Icon $pointer onClick={handleAddCustomizedPriceRow}>
                        <PlusIcon />
                      </Icon>
                    )}
                  </Div>

                  {optionCustomizedPrices.length > 0 && (
                    <Div $flex $column $gap="8px" $gapSM="12px">
                      {optionCustomizedPrices.map((cp, i) => {
                        const filteredProviders = allProviders.filter(
                          (p) =>
                            p === cp.provider ||
                            !optionCustomizedPrices.some(
                              (other) => other.provider === p
                            )
                        );
                        return (
                          <Div
                            $flex
                            $alignCenter
                            $gap="16px"
                            $width="100%"
                            key={i}
                          >
                            <Div
                              $flex
                              $alignCenter
                              $gap="16px"
                              $columnSM
                              $gapSM="4px"
                              $width="100%"
                            >
                              <Div $width="100%">
                                <AdminCustomProviderSelect
                                  value={cp.provider}
                                  onChange={(v) =>
                                    handleChangeCustomizedPriceProvider(
                                      i,
                                      v as Provider | null
                                    )
                                  }
                                  providerArray={filteredProviders}
                                  placeholder="Select Provider"
                                  emptyPlaceholder="No more providers to add"
                                  $shadowDesign={false}
                                />
                              </Div>
                              <Div $width="100%">
                                <AdminDollarInput2
                                  $textCenter
                                  onValueChange={(v: number) =>
                                    handleChangeCustomizedPricePrice(i, v)
                                  }
                                  value={cp.price}
                                />
                              </Div>
                            </Div>
                            <Icon
                              $pointer
                              onClick={() => handleRemoveCustomizedPrice(i)}
                            >
                              <TrashIconRed />
                            </Icon>
                          </Div>
                        );
                      })}
                    </Div>
                  )}
                </Div>

                <Div $flex $gap="16px" $alignCenter $justifyEnd $width="100%">
                  <Button
                    $selected
                    $gray
                    onClick={
                      editingOption !== null
                        ? handleEditCancelExpand
                        : handleExpand
                    }
                  >
                    Cancel
                  </Button>
                  <Button
                    $selected
                    onClick={
                      editingOption !== null
                        ? handleEditOption
                        : handleAddOption
                    }
                  >
                    {editingOption !== null ? 'Save' : 'Add'}
                  </Button>
                </Div>
              </Div>
            </div>
          </Collapsible>

          {options.length > 0 && (
            <SortableModifierOptions
              options={options}
              onReorder={setOptions}
              hiddenIndex={editingOption}
              sortable={options.length > 1 && editingOption === null}
              onRemove={handleRemoveOption}
              onEdit={handleEditExpand}
            />
          )}
        </div>

        {optionGroup === ModifierType.Option && (
          <Div $flex $columnSM $alignCenter $gapSM="16px" $gap="24px">
            <AdminSelect
              value={isRequired}
              options={requiredOptionsGroup}
              onChange={({ target }: { target: HTMLSelectElement }) =>
                handleChangeIsRequired(target.value as 'true' | 'false')
              }
              label="Required Choice?"
              $textCenter
            />
            <AdminSelect
              value={choices}
              options={choicesOptionsGroup}
              onChange={({ target }: { target: HTMLSelectElement }) => {
                handleChangeChoices(target.value);
              }}
              label="How many choices can customer select?"
              $textCenter
            />
          </Div>
        )}

        {optionGroup === ModifierType.Option && choices === 'Set a min-max' && (
          <Div $flex $alignCenter $gap="16px" $width="100%">
            <AdminInput
              placeholder="Enter Here"
              label="Minimum Qty"
              $textCenter
              value={selectionsMin}
              onChange={({ target }: { target: HTMLInputElement }) =>
                setSelectionsMin(target.value)
              }
              type="number"
              min={required ? '1' : '2'}
            />
            <AdminInput
              placeholder="Enter Here"
              label="Maximum Qty"
              $textCenter
              value={selectionsMax}
              onChange={({ target }: { target: HTMLInputElement }) =>
                setSelectionsMax(target.value)
              }
              type="number"
            />
          </Div>
        )}
        <Div $flex $columnSM $alignCenter $gapSM="16px" $gap="24px">
          <Button $selected $gray $w="100%" onClick={() => closeModal?.()}>
            Cancel
          </Button>
          <Button $selected $w="100%" onClick={handleAddModifierGroup}>
            Confirm
          </Button>
        </Div>
      </Div>
    </Modal>
  );
};
